/**
 * crm/IntakeTokensCard.jsx — Phase 75 refactor
 * Extracted from CRMShell.jsx (Phase 74). Issue / list / revoke no-auth
 * public intake tokens. URLs hit POST /api/public/crm/intake/{token}.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export function IntakeTokensCard({ isAdmin }) {
  const [tokens, setTokens] = useState(null);
  const [label, setLabel] = useState("");
  const [source, setSource] = useState("Website");
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/crm/intake-tokens");
      setTokens(data || []);
    } catch (e) { toast.error(formatApiError(e)); setTokens([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!label.trim()) return;
    setCreating(true);
    try {
      await api.post("/crm/intake-tokens", { label: label.trim(), source: source.trim() || label.trim() });
      setLabel(""); setSource("Website");
      toast.success("Token issued");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCreating(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this intake token? Any form using it will break.")) return;
    try {
      await api.delete(`/crm/intake-tokens/${id}`);
      toast.success("Revoked");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const buildUrl = (tok) => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    return `${base}/api/public/crm/intake/${tok}`;
  };

  const copy = async (tok, id) => {
    try {
      await navigator.clipboard.writeText(buildUrl(tok));
      setCopiedId(id);
      setTimeout(() => setCopiedId((c) => c === id ? null : c), 1500);
    } catch { toast.error("Copy failed — long-press the URL instead."); }
  };

  if (!tokens) return null;

  return (
    <div className="card-flat overflow-hidden" data-testid="crm-intake-tokens-card">
      <div className="px-4 py-3 border-b border-slate-200 flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="font-display text-sm font-semibold text-slate-900">Public lead-capture tokens</div>
          <div className="text-xs text-slate-500">Each token gives one external form (contact page, conference scanner, LinkedIn outreach tool) a no-auth POST URL that drops a lead into <b>New Inquiry</b>. Honeypot + ingress rate-limiting are baked in.</div>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-1.5">
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label · e.g. Website contact" className="h-8 rounded-sm text-sm w-44" data-testid="crm-intake-label" />
            <Input value={source} onChange={(e) => setSource(e.target.value)} placeholder="Source tag" className="h-8 rounded-sm text-sm w-32" data-testid="crm-intake-source" />
            <Button size="sm" onClick={create} disabled={creating || !label.trim()} className="rounded-sm h-8 bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="crm-intake-issue">
              {creating ? <Loader2 size={11} className="mr-1 animate-spin" /> : <Plus size={11} className="mr-1" />} Issue
            </Button>
          </div>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-2 text-left">Label</th>
              <th className="px-4 py-2 text-left">Source</th>
              <th className="px-4 py-2 text-left">URL</th>
              <th className="px-4 py-2 text-right">Intakes</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {tokens.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400 italic">No intake tokens yet.</td></tr>
            )}
            {tokens.map((t) => (
              <tr key={t.id} className="border-t border-slate-100" data-testid={`crm-intake-row-${t.id}`}>
                <td className="px-4 py-2 font-medium text-slate-900">{t.label}</td>
                <td className="px-4 py-2"><span className="text-[10px] bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded-sm">{t.source}</span></td>
                <td className="px-4 py-2">
                  <code className="text-[10px] bg-slate-50 px-1.5 py-0.5 rounded text-slate-700 break-all">{buildUrl(t.token)}</code>
                </td>
                <td className="px-4 py-2 text-right text-slate-700">{t.intake_count || 0}</td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  <button onClick={() => copy(t.token, t.id)} className="text-[10px] text-blue-600 hover:underline mr-2" data-testid={`crm-intake-copy-${t.id}`}>
                    {copiedId === t.id ? "Copied!" : "Copy URL"}
                  </button>
                  {isAdmin && (
                    <button onClick={() => revoke(t.id)} className="text-[10px] text-rose-600 hover:underline" data-testid={`crm-intake-revoke-${t.id}`}>
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
